import { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import SoMSubNav from '../components/SoMSubNav';
import { clientsAPI, somPromptsAPI } from '../lib/api';
import {
  MAX_SOM_CUSTOM_PROMPT_TEXT,
  canRunNowPrompt,
  customPromptPayload,
  somPromptApiError,
  splitSomPrompts,
  validatePromptText,
} from '../lib/somPromptHelpers';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Lock, Plus, Eye, Warning, PencilSimple, Archive, ArrowCounterClockwise, Play } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMPTY_FORM = { prompt_text: '', market: '', language: 'en', priority: 'medium' };

function StatusBadge({ active }) {
  return (
    <span
      className={`px-2 py-0.5 text-[10px] font-mono uppercase rounded ${
        active ? 'bg-[var(--success-bg)] text-[var(--success)]' : 'bg-[var(--neutral-bg)] text-[var(--text-secondary)]'
      }`}
    >
      {active ? 'Active' : 'Archived'}
    </span>
  );
}

export default function SoMPromptsPage() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [tab, setTab] = useState('canonical');
  const [showArchived, setShowArchived] = useState(false);
  const [canonical, setCanonical] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewPrompt, setViewPrompt] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [mutating, setMutating] = useState(false);
  const [runTarget, setRunTarget] = useState(null);
  const [runningId, setRunningId] = useState('');
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState('');

  useEffect(() => {
    clientsAPI.list()
      .then((r) => {
        setClients(r.data || []);
        if (r.data?.length) setClientId(r.data[0].id);
      })
      .catch((err) => toast.error(somPromptApiError(err)));
  }, []);

  const loadPrompts = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setLoadError('');
    try {
      const res = await somPromptsAPI.list(clientId, showArchived);
      const split = splitSomPrompts(res.data?.prompts || []);
      setCanonical(split.canonical);
      setCustom(split.custom);
    } catch (err) {
      const msg = somPromptApiError(err);
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [clientId, showArchived]);

  useEffect(() => { loadPrompts(); }, [loadPrompts]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (prompt) => {
    setEditing(prompt);
    setForm({
      prompt_text: prompt.prompt_text || '',
      market: prompt.market || '',
      language: prompt.language || 'en',
      priority: prompt.priority || 'medium',
    });
    setFormError('');
    setFormOpen(true);
  };

  const submitForm = async () => {
    const textError = validatePromptText(form.prompt_text);
    if (textError) {
      setFormError(textError);
      return;
    }
    if (!clientId) {
      toast.error('Select a client first.');
      return;
    }
    setSaving(true);
    try {
      const payload = customPromptPayload(form);
      if (editing) {
        await somPromptsAPI.updateCustom(clientId, editing.id, payload);
        toast.success('Custom prompt updated.');
      } else {
        await somPromptsAPI.createCustom(clientId, payload);
        toast.success('Custom prompt created.');
      }
      setFormOpen(false);
      setEditing(null);
      await loadPrompts();
    } catch (err) {
      toast.error(somPromptApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget || mutating) return;
    setMutating(true);
    try {
      await somPromptsAPI.deactivate(clientId, archiveTarget.id);
      toast.success('Custom prompt archived.');
      setArchiveTarget(null);
      await loadPrompts();
    } catch (err) {
      toast.error(somPromptApiError(err));
    } finally {
      setMutating(false);
    }
  };

  const restorePrompt = async (prompt) => {
    if (mutating) return;
    setMutating(true);
    try {
      await somPromptsAPI.activate(clientId, prompt.id);
      toast.success('Custom prompt restored.');
      await loadPrompts();
    } catch (err) {
      toast.error(somPromptApiError(err));
    } finally {
      setMutating(false);
    }
  };

  const confirmRunNow = async () => {
    if (!runTarget || runningId) return;
    const promptId = runTarget.id;
    setRunningId(promptId);
    setRunError('');
    setRunResult(null);
    try {
      const res = await somPromptsAPI.runNow(clientId, promptId);
      const data = res.data || {};
      setRunTarget(null);
      if (data.status === 'completed') {
        setRunResult(data);
        toast.success('Run Now completed.');
      } else {
        const msg = data.error || data.error_code || 'Provider failed to complete this run.';
        setRunError(msg);
        toast.error(msg);
      }
    } catch (err) {
      setRunTarget(null);
      const msg = somPromptApiError(err);
      setRunError(msg);
      toast.error(msg);
    } finally {
      setRunningId('');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="som-prompts-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">
              SoM Prompts
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
              Manage the prompts used to monitor how your brand appears in AI-generated recommendations.
            </p>
          </div>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-full sm:w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-prompts-client-select">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <SoMSubNav />

        <div className="flex flex-wrap items-center gap-2" data-testid="som-prompts-tabs">
          <button
            type="button"
            data-testid="som-prompts-tab-canonical"
            onClick={() => setTab('canonical')}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === 'canonical' ? 'bg-[var(--accent-light)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]'}`}
          >
            Canonical
          </button>
          <button
            type="button"
            data-testid="som-prompts-tab-custom"
            onClick={() => setTab('custom')}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === 'custom' ? 'bg-[var(--accent-light)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]'}`}
          >
            Custom
          </button>
        </div>

        {loadError && (
          <div className="rounded-lg border border-[var(--error-border)] bg-[var(--error-bg)] px-4 py-3 text-sm text-[var(--error)]" data-testid="som-prompts-error">
            {loadError}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : !clientId ? (
          <div className="nx-metric-card rounded-lg text-center py-12">
            <p className="text-[var(--text-secondary)]">Select a client to manage SoM prompts.</p>
          </div>
        ) : tab === 'canonical' ? (
          <CanonicalSection prompts={canonical} onView={setViewPrompt} />
        ) : (
          <CustomSection
            prompts={custom}
            showArchived={showArchived}
            onToggleArchived={setShowArchived}
            onAdd={openCreate}
            onEdit={openEdit}
            onArchive={setArchiveTarget}
            onRestore={restorePrompt}
            onRunNow={setRunTarget}
            runningId={runningId}
          />
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)] max-w-lg" data-testid="som-custom-prompt-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>
              {editing ? 'Edit Custom Prompt' : 'Add Custom Prompt'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Prompt text *</label>
              <Textarea
                data-testid="som-custom-prompt-text"
                value={form.prompt_text}
                maxLength={MAX_SOM_CUSTOM_PROMPT_TEXT}
                onChange={(e) => {
                  setForm({ ...form, prompt_text: e.target.value });
                  setFormError(validatePromptText(e.target.value));
                }}
                placeholder="How should AI systems describe your brand?"
                className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] min-h-[120px]"
              />
              {formError && <p className="text-xs text-[var(--error)] mt-1" data-testid="som-custom-prompt-text-error">{formError}</p>}
              <p className="text-[10px] text-[var(--text-tertiary)] mt-1 font-mono">{form.prompt_text.trim().length}/{MAX_SOM_CUSTOM_PROMPT_TEXT}</p>
            </div>
            <div>
              <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Market</label>
              <Input
                data-testid="som-custom-prompt-market"
                value={form.market}
                onChange={(e) => setForm({ ...form, market: e.target.value })}
                placeholder="Optional"
                className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Language</label>
                <Input
                  data-testid="som-custom-prompt-language"
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"
                />
              </div>
              <div>
                <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-custom-prompt-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                    {['low', 'medium', 'high'].map((p) => (
                      <SelectItem key={p} value={p} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)] capitalize">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              data-testid="som-custom-prompt-save"
              disabled={saving}
              onClick={submitForm}
              className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            >
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create prompt'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewPrompt} onOpenChange={(open) => { if (!open) setViewPrompt(null); }}>
        <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-canonical-view-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>Production prompt</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--text-primary)]">{viewPrompt?.prompt_text}</p>
          <p className="text-xs text-[var(--text-tertiary)] font-mono">Order {viewPrompt?.metadata?.order} · Locked</p>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
        <AlertDialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this custom prompt?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">
              Archived custom prompts can be restored later and are not used by the production SoM check.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[var(--border)] text-[var(--text-secondary)]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="som-archive-confirm-btn"
              className="bg-[var(--error)] hover:opacity-90 text-white"
              onClick={confirmArchive}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!runTarget} onOpenChange={(open) => { if (!open && !runningId) setRunTarget(null); }}>
        <AlertDialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-run-now-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Run this custom prompt now?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">
              This executes one custom prompt. It does not update the production SoM score.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[var(--border)] text-[var(--text-secondary)]" disabled={!!runningId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="som-run-now-confirm-btn"
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              disabled={!!runningId}
              onClick={confirmRunNow}
            >
              {runningId ? 'Running…' : 'Run Now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!runResult} onOpenChange={(open) => { if (!open) setRunResult(null); }}>
        <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-run-now-result">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>Run Now result</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-[var(--text-primary)]">
            <p data-testid="som-run-now-result-status">Status: {runResult?.status}</p>
            <p className="text-[var(--text-secondary)]">{runResult?.response_preview || 'No preview.'}</p>
            <p className="text-xs font-mono text-[var(--text-tertiary)]">
              Provider {runResult?.provider} · {runResult?.latency_ms}ms · {runResult?.citations_created || 0} citations
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!runError && !runTarget} onOpenChange={(open) => { if (!open) setRunError(''); }}>
        <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-run-now-error">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>Run Now failed</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--error)]">{runError}</p>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function CanonicalSection({ prompts, onView }) {
  if (!prompts.length) {
    return (
      <div className="nx-metric-card rounded-lg text-center py-12" data-testid="som-canonical-empty">
        <Warning size={36} className="mx-auto mb-3 text-[var(--warning)]" />
        <p className="text-[var(--text-primary)] font-semibold">Production prompts unavailable</p>
        <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-md mx-auto">
          The locked production set could not be loaded. Custom prompts cannot replace this configuration.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="som-canonical-section">
      <div className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-light)] px-4 py-3 text-sm text-[var(--accent)]" data-testid="som-canonical-banner">
        These four prompts power the production SoM visibility check. They are locked to protect scoring consistency.
      </div>
      <div className="nx-metric-card rounded-lg overflow-x-auto">
        <table className="nx-table min-w-[640px]">
          <thead>
            <tr>
              <th>Order</th>
              <th>Prompt</th>
              <th>Status</th>
              <th>Type</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {prompts.map((p) => (
              <tr key={p.id} data-testid={`som-canonical-row-${p.metadata?.order}`}>
                <td className="font-mono text-[var(--text-primary)]">{p.metadata?.order}</td>
                <td className="text-[var(--text-primary)] max-w-xl">{p.prompt_text}</td>
                <td>
                  <StatusBadge active={p.active !== false} />
                </td>
                <td>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase rounded bg-[var(--warning-bg)] text-[var(--warning)]">
                    <Lock size={10} /> Production · Locked
                  </span>
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[var(--text-secondary)] h-7 px-2"
                    data-testid={`som-canonical-view-${p.metadata?.order}`}
                    onClick={() => onView(p)}
                  >
                    <Eye size={14} className="mr-1" /> View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomSection({ prompts, showArchived, onToggleArchived, onAdd, onEdit, onArchive, onRestore, onRunNow, runningId }) {
  return (
    <div className="space-y-3" data-testid="som-custom-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]" data-testid="som-show-archived">
          <Switch checked={showArchived} onCheckedChange={onToggleArchived} />
          Show archived
        </label>
        <Button data-testid="som-add-custom-prompt" onClick={onAdd} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
          <Plus size={16} className="mr-2" /> Add Custom Prompt
        </Button>
      </div>
      {!prompts.length ? (
        <div className="nx-metric-card rounded-lg text-center py-12" data-testid="som-custom-empty">
          <p className="text-[var(--text-primary)] font-semibold">No custom prompts yet</p>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-lg mx-auto">
            Create additional prompts to explore how AI systems describe and recommend your market. Custom prompts do not affect the production SoM score.
          </p>
          <Button data-testid="som-add-custom-prompt-empty" onClick={onAdd} className="mt-4 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
            <Plus size={16} className="mr-2" /> Add Custom Prompt
          </Button>
        </div>
      ) : (
        <div className="nx-metric-card rounded-lg overflow-x-auto">
          <table className="nx-table min-w-[720px]">
            <thead>
              <tr>
                <th>Prompt</th>
                <th>Market</th>
                <th>Language</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {prompts.map((p) => (
                <tr key={p.id} data-testid={`som-custom-row-${p.id}`}>
                  <td className="text-[var(--text-primary)] max-w-md">{p.prompt_text}</td>
                  <td>{p.market || '—'}</td>
                  <td className="font-mono">{p.language || 'en'}</td>
                  <td className="capitalize">{p.priority || 'medium'}</td>
                  <td><StatusBadge active={p.active !== false} /></td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="ghost" className="text-[var(--text-secondary)] h-7 px-2" data-testid={`som-custom-edit-${p.id}`} onClick={() => onEdit(p)}>
                        <PencilSimple size={14} className="mr-1" /> Edit
                      </Button>
                      {canRunNowPrompt(p) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[var(--success)] h-7 px-2"
                          data-testid={`som-custom-run-${p.id}`}
                          disabled={!!runningId}
                          onClick={() => onRunNow(p)}
                        >
                          <Play size={14} className="mr-1" /> {runningId === p.id ? 'Running…' : 'Run Now'}
                        </Button>
                      )}
                      {p.active !== false ? (
                        <Button size="sm" variant="ghost" className="text-[var(--warning)] h-7 px-2" data-testid={`som-custom-archive-${p.id}`} onClick={() => onArchive(p)}>
                          <Archive size={14} className="mr-1" /> Archive
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-[var(--success)] h-7 px-2" data-testid={`som-custom-restore-${p.id}`} onClick={() => onRestore(p)}>
                          <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
